/**
 * Modal Stack Manager — Wave 22.13
 *
 * Host-level manager for plugin-initiated modals.
 * Maintains an ordered stack of active modals, resolves the promise
 * returned by `ui.openModal()` when the modal closes, and exposes
 * DevTools-safe snapshots for the Interaction Inspector.
 *
 * This module is framework-agnostic (plain TypeScript + Zustand).
 * React integration is handled by `PluginModalHost` component.
 */

import { create } from 'zustand';
import type {
  ModalRequest,
  ModalResult,
  ActiveModal,
  ActiveModalSnapshot,
  UIInvocationLogEntry,
} from '../types/plugin-interaction';
import { invocationLog } from './interaction-invocation-log';

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

let _nextModalId = 1;
function generateModalId(): string {
  return `modal-${Date.now()}-${_nextModalId++}`;
}

// ────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────

interface ModalStackState {
  /** Ordered stack: last entry is topmost. */
  stack: ActiveModal[];

  /** Open a modal, returning a promise that resolves when it closes. */
  openModal: (request: ModalRequest) => Promise<ModalResult>;

  /** Close the topmost modal (or a specific one by id) with a result. */
  closeModal: (result: ModalResult, modalId?: string) => void;

  /** DevTools-safe snapshot (strips internal callbacks). */
  getSnapshots: () => ActiveModalSnapshot[];

  /** Clear all modals (e.g. on route change). */
  clearAll: () => void;
}

export const useModalStackStore = create<ModalStackState>((set, get) => ({
  stack: [],

  openModal: (request: ModalRequest): Promise<ModalResult> => {
    return new Promise<ModalResult>((resolve) => {
      const modal: ActiveModal = {
        id: generateModalId(),
        pluginId: request.pluginId,
        viewId: request.viewId,
        props: request.props,
        openedAt: new Date().toISOString(),
        _resolve: resolve,
      };

      // Wave 22.13.2: Trace at modal stack push boundary
      console.debug(`[ModalStack] pushing modal`, { modalId: modal.id, pluginId: request.pluginId, viewId: request.viewId });
      invocationLog.append({
        kind: 'modal-stack-push',
        pluginId: request.pluginId,
        detail: { modalId: modal.id, viewId: request.viewId, props: request.props },
      });

      set((state) => ({ stack: [...state.stack, modal] }));

      invocationLog.append({
        kind: 'modal-open',
        pluginId: request.pluginId,
        detail: { modalId: modal.id, viewId: request.viewId, props: request.props },
      });
    });
  },

  closeModal: (result: ModalResult, modalId?: string) => {
    const { stack } = get();
    const targetIdx = modalId
      ? stack.findIndex((m) => m.id === modalId)
      : stack.length - 1;

    if (targetIdx < 0) return;

    const modal = stack[targetIdx];
    modal._resolve(result);

    set((state) => ({
      stack: state.stack.filter((_, i) => i !== targetIdx),
    }));

    invocationLog.append({
      kind: 'modal-close',
      pluginId: modal.pluginId,
      detail: { modalId: modal.id, viewId: modal.viewId, confirmed: result.confirmed },
    });
  },

  getSnapshots: (): ActiveModalSnapshot[] => {
    return get().stack.map(({ id, pluginId, viewId, props, openedAt }) => ({
      id,
      pluginId,
      viewId,
      props,
      openedAt,
    }));
  },

  clearAll: () => {
    const { stack } = get();
    for (const modal of stack) {
      modal._resolve({ confirmed: false });
    }
    set({ stack: [] });
  },
}));

// ────────────────────────────────────────────────────────
// Convenience (non-React) accessors
// ────────────────────────────────────────────────────────

/** Open a modal from non-React code. */
export function openModal(request: ModalRequest): Promise<ModalResult> {
  return useModalStackStore.getState().openModal(request);
}

/** Close the topmost modal from non-React code. */
export function closeModal(result: ModalResult, modalId?: string): void {
  useModalStackStore.getState().closeModal(result, modalId);
}
